import React from 'react';
import { X, TrendingUp, TrendingDown } from 'lucide-react';

const fmt = (v, decimals = 2) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
};

const fmtBig = (v) => {
  if (!v) return '—';
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  return fmt(v, 0);
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
};

const Row = ({ label, value, color }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
    <span className="text-cap-text-muted text-xs">{label}</span>
    <span className={`text-sm font-mono ${color || 'text-white'}`}>{value}</span>
  </div>
);

const FinancialPanel = ({ data, onClose }) => {
  if (!data) {
    return (
      <div className="w-72 bg-cap-bg-subtle border-l border-white/5 p-4 flex items-center justify-center" data-testid="financial-panel">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-cap-emerald border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-cap-text-muted text-sm">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 bg-cap-bg-subtle border-l border-white/5 overflow-y-auto flex-shrink-0" data-testid="financial-panel">
      <div className="p-4 border-b border-white/5 flex items-center justify-between">
        <h3 className="text-white font-display font-bold text-sm">Données Financières</h3>
        <button onClick={onClose} className="text-cap-text-muted hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="p-4 space-y-4">
        {/* Company Info */}
        <div>
          <p className="text-white font-medium text-sm truncate">{data.name || data.symbol}</p>
          <p className="text-cap-text-muted text-xs">
            {[data.sector, data.country].filter(Boolean).join(' | ')}
          </p>
        </div>

        {/* Price */}
        <div className="bg-cap-bg-elevated rounded-lg p-3">
          <p className="text-2xl font-mono font-bold text-white">{fmt(data.price)}</p>
          <p className="text-cap-text-muted text-xs">{data.currency}</p>
        </div>

        {/* Valuation */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Valorisation</p>
          <Row label="Market Cap" value={fmtBig(data.marketCap)} />
          <Row label="PER (trailing)" value={fmt(data.trailingPE)} />
          <Row label="PER (forward)" value={fmt(data.forwardPE)} />
          <Row label="EPS" value={fmt(data.eps)} />
          <Row label="Beta" value={fmt(data.beta)} />
          <Row label="PEG Ratio" value={fmt(data.pegRatio)} />
        </div>

        {/* Margins */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Marges</p>
          <Row label="Marge brute" value={fmtPct(data.grossMargin)} />
          <Row label="Marge op." value={fmtPct(data.operatingMargin)} />
          <Row label="Marge nette" value={fmtPct(data.profitMargin)} />
        </div>

        {/* Growth */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Croissance</p>
          <Row label="Revenue Growth" value={fmtPct(data.revenueGrowth)} color={data.revenueGrowth > 0 ? 'text-green-400' : 'text-red-400'} />
          <Row label="Earnings Growth" value={fmtPct(data.earningsGrowth)} color={data.earningsGrowth > 0 ? 'text-green-400' : 'text-red-400'} />
        </div>

        {/* Financial Health */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Sante Financiere</p>
          <Row label="Debt/Equity" value={fmt(data.debtToEquity)} />
          <Row label="Current Ratio" value={fmt(data.currentRatio)} />
          <Row label="ROE" value={fmtPct(data.returnOnEquity)} />
          <Row label="ROA" value={fmtPct(data.returnOnAssets)} />
        </div>

        {/* Price Range */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Fourchette 52 semaines</p>
          <Row label="52W Low" value={fmt(data.fiftyTwoWeekLow)} color="text-red-400" />
          <Row label="52W High" value={fmt(data.fiftyTwoWeekHigh)} color="text-green-400" />
          <Row label="Dividende" value={data.dividendYield ? fmtPct(data.dividendYield) : '—'} />
        </div>

        {/* Cash & Revenue */}
        <div>
          <p className="text-cap-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Fondamentaux</p>
          <Row label="Revenus" value={fmtBig(data.totalRevenue)} />
          <Row label="EBITDA" value={fmtBig(data.ebitda)} />
          <Row label="FCF" value={fmtBig(data.freeCashflow)} />
          <Row label="Cash total" value={fmtBig(data.totalCash)} />
          <Row label="Dette totale" value={fmtBig(data.totalDebt)} />
        </div>
      </div>
    </div>
  );
};

export default FinancialPanel;
